/**
 * Rules and messages of the guided "Set Your Password" screen (Password and Sample Data Journey
 * Improvements, Point 1). Pure, so every state of the four reference screens can be tested.
 */

export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 20

export type PasswordRule = 'length' | 'capital' | 'number' | 'symbol'

/** Checklist order. */
export const PASSWORD_RULES: PasswordRule[] = ['length', 'capital', 'number', 'symbol']

export type PasswordChecks = Record<PasswordRule, boolean>

/**
 * A correction for one unmet rule. Length says which way it is off, so the message names the
 * actual problem.
 */
export type PasswordFix = 'lengthShort' | 'lengthLong' | 'capital' | 'number' | 'symbol'

export function checkPassword(value: string): PasswordChecks {
  return {
    length: value.length >= PASSWORD_MIN_LENGTH && value.length <= PASSWORD_MAX_LENGTH,
    capital: /[A-Z]/.test(value),
    number: /\d/.test(value),
    symbol: /[^A-Za-z0-9]/.test(value),
  }
}

const fixOf = (rule: PasswordRule, value: string): PasswordFix => {
  if (rule !== 'length') return rule
  return value.length > PASSWORD_MAX_LENGTH ? 'lengthLong' : 'lengthShort'
}

export type PasswordGuidance = {
  checks: PasswordChecks
  /** Unmet rules, in checklist order. */
  unmet: PasswordRule[]
  passwordValid: boolean
  /** Continue is enabled once both fields contain text; it validates before it submits. */
  canPressContinue: boolean
  /** Every rule met and the confirmation equal: Continue saves. */
  canSubmit: boolean
  /** Beneath Confirm Password: green only for a valid, matching pair; never for an invalid one. */
  match: 'match' | 'mismatch' | null
  /**
   * Beneath Create Password after an invalid Continue: the one correction left, or `several`
   * when more than one rule is unmet (the checklist marks each).
   */
  createError: PasswordFix | 'several' | null
  /** Beside Continue after an invalid Continue: what to do to continue. */
  continueHint: PasswordFix | 'several' | null
  /** Where an invalid Continue takes the user: the first field that needs a correction. */
  firstInvalidField: 'password' | 'confirm' | null
}

/**
 * @param password Create Password
 * @param confirm Confirm Password
 * @param attempted Continue was pressed while the entries were not valid. Leaving a field is not
 *   an attempt: the extra errors (Screen 3) appear only after an invalid Continue.
 */
export function passwordGuidance(
  password: string,
  confirm: string,
  attempted: boolean
): PasswordGuidance {
  const checks = checkPassword(password)
  const unmet = PASSWORD_RULES.filter((rule) => !checks[rule])
  const passwordValid = unmet.length === 0
  const matches = confirm.length > 0 && confirm === password
  const canSubmit = passwordValid && matches

  // A valid original with a different confirmation says so at once, beneath Confirm Password —
  // also while the confirmation is still a correct beginning of the password.
  const mismatchShown = passwordValid && confirm.length > 0 && !matches

  const single = unmet.length === 1 ? fixOf(unmet[0], password) : null
  const passwordFix: PasswordFix | 'several' | null = passwordValid ? null : (single ?? 'several')

  return {
    checks,
    unmet,
    passwordValid,
    canPressContinue: password.length > 0 && confirm.length > 0,
    canSubmit,
    match: passwordValid && matches ? 'match' : mismatchShown ? 'mismatch' : null,
    createError: attempted ? passwordFix : null,
    // A mismatch alone is said once, beneath Confirm Password ("Passwords don't match"); a second
    // line beside Continue would only repeat it.
    continueHint: !attempted || canSubmit ? null : passwordFix,
    firstInvalidField: canSubmit ? null : passwordValid ? 'confirm' : 'password',
  }
}
